import { Component, OnInit } from "@angular/core"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { MatFormField, MatInputModule } from "@angular/material/input"
import { MatIconModule } from "@angular/material/icon"
import { MatRadioModule } from "@angular/material/radio"
import { MatSelectModule } from "@angular/material/select"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { Router, RouterModule } from "@angular/router"
import { AuthService } from "../../services/auth.service"
import { User } from "../../models/user.model"
import { AuthFloatLabelDirective } from "../../directives/auth-float-label.directive"
import { collapseRepeatedSpaces } from "../../utils/text"
import {
  SIGNUP_PURPOSE_OTHER,
  SIGNUP_PURPOSE_OTHER_MAX,
  SignupPurposeCategoriesService,
  SignupPurposeCategory,
} from "../../services/signup-purpose-categories.service"

@Component({
  selector: "app-signup",
  imports: [
    MatFormField,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    RouterModule,
    ReactiveFormsModule,
    AuthFloatLabelDirective,
  ],
  templateUrl: "./signup.component.html",
  styleUrl: "./signup.component.css",
})
export class SignupComponent implements OnInit {
  signupForm: FormGroup
  phoneOtpForm: FormGroup
  loading = false
  error = ""
  showPassword = false
  showConfirmPassword = false

  phoneVerificationRequired = false
  pendingEmail = ""
  phoneOtpError = ""
  phoneVerifyLoading = false
  resendLoading = false
  countdown = 0
  canResend = false
  private countdownTimer: ReturnType<typeof setInterval> | null = null

  purposeCategories: SignupPurposeCategory[] = []
  loadingPurposeCategories = false
  readonly purposeOtherSentinel = SIGNUP_PURPOSE_OTHER
  readonly purposeOtherMax = SIGNUP_PURPOSE_OTHER_MAX

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private purposeCategoriesService: SignupPurposeCategoriesService,
  ) {
    this.signupForm = this.formBuilder.group(
      {
        accounttype: ["", [Validators.required]],
        companyname: [""],
        signup_purpose_category_id: [""],
        signup_purpose_other: ["", [Validators.maxLength(SIGNUP_PURPOSE_OTHER_MAX)]],
        firstname: ["", [Validators.required, Validators.minLength(2)]],
        lastname: ["", [Validators.required, Validators.minLength(2)]],
        email: ["", [Validators.email, Validators.required]],
        password: ["", [Validators.minLength(8), Validators.required]],
        confirmPassword: ["", [Validators.minLength(8), Validators.required]],
        phonenumber: ["", [Validators.pattern(/^\+?[\d\s-]+$/)]],
        textnotifications: [false],
      },
      { validators: [this.passwordMatchValidator, this.companyNameValidator, this.purposeOtherValidator] },
    )

    this.phoneOtpForm = this.formBuilder.group({
      otp: ["", [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    })

    this.signupForm.get("accounttype")?.valueChanges.subscribe((accountType) => {
      const companyNameControl = this.signupForm.get("companyname")
      if (accountType === "business") {
        companyNameControl?.setValidators([Validators.required, Validators.minLength(2)])
        this.signupForm.patchValue(
          { signup_purpose_category_id: "", signup_purpose_other: "" },
          { emitEvent: false },
        )
      } else {
        companyNameControl?.clearValidators()
      }
      companyNameControl?.updateValueAndValidity()
      this.syncPurposeValidators(accountType, this.signupForm.get("signup_purpose_category_id")?.value)
    })

    this.signupForm.get("signup_purpose_category_id")?.valueChanges.subscribe((value) => {
      this.syncPurposeValidators(this.signupForm.get("accounttype")?.value, value)
    })
  }

  ngOnInit(): void {
    this.loadPurposeCategories()
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get("password")
    const confirmPassword = form.get("confirmPassword")

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true })
      return { passwordMismatch: true }
    }

    return null
  }

  companyNameValidator(form: FormGroup) {
    const accountType = form.get("accounttype")
    const companyName = form.get("companyname")

    if (accountType?.value === "business" && (!companyName?.value || companyName.value.trim().length < 2)) {
      companyName?.setErrors({ required: true })
      return { companyNameRequired: true }
    }

    return null
  }

  purposeOtherValidator(form: FormGroup) {
    const accountType = form.get("accounttype")
    const purpose = form.get("signup_purpose_category_id")
    const other = form.get("signup_purpose_other")

    if (accountType?.value !== "individual") {
      return null
    }
    if (purpose?.value === SIGNUP_PURPOSE_OTHER) {
      const text = collapseRepeatedSpaces(other?.value || "")
      if (!text) {
        return { purposeOtherRequired: true }
      }
      if (text.length > SIGNUP_PURPOSE_OTHER_MAX) {
        return { purposeOtherMax: true }
      }
    }
    return null
  }

  get showPurposeDropdown(): boolean {
    return this.accountTypeControl?.value === "individual"
  }

  get isPurposeOtherSelected(): boolean {
    return this.showPurposeDropdown && this.signupPurposeControl?.value === SIGNUP_PURPOSE_OTHER
  }

  get purposeOtherLength(): number {
    return String(this.signupPurposeOtherControl?.value || "").length
  }

  private loadPurposeCategories(): void {
    this.loadingPurposeCategories = true
    this.purposeCategoriesService.getPublicCategories().subscribe({
      next: (categories) => {
        this.purposeCategories = categories
        this.loadingPurposeCategories = false
      },
      error: () => {
        this.purposeCategories = []
        this.loadingPurposeCategories = false
      },
    })
  }

  private syncPurposeValidators(accountType: unknown, purposeValue: unknown): void {
    const purposeControl = this.signupForm.get("signup_purpose_category_id")
    const otherControl = this.signupForm.get("signup_purpose_other")
    if (!purposeControl || !otherControl) return

    if (accountType === "individual") {
      purposeControl.setValidators([Validators.required])
      if (purposeValue === SIGNUP_PURPOSE_OTHER) {
        otherControl.setValidators([Validators.required, Validators.maxLength(SIGNUP_PURPOSE_OTHER_MAX)])
      } else {
        otherControl.setValidators([Validators.maxLength(SIGNUP_PURPOSE_OTHER_MAX)])
        if (purposeValue && purposeValue !== SIGNUP_PURPOSE_OTHER && otherControl.value) {
          otherControl.setValue("", { emitEvent: false })
        }
      }
    } else {
      purposeControl.clearValidators()
      otherControl.clearValidators()
    }
    purposeControl.updateValueAndValidity({ emitEvent: false })
    otherControl.updateValueAndValidity({ emitEvent: false })
  }

  onSubmit(): void {
    const textNotifications = this.signupForm.get("textnotifications")?.value
    const phoneControl = this.signupForm.get("phonenumber")
    const phone = (phoneControl?.value ?? "").trim()

    if (textNotifications && !phone) {
      phoneControl?.setErrors({ phoneRequiredForNotifications: true })
      phoneControl?.markAsTouched()
      return
    }

    if (phoneControl?.hasError("phoneRequiredForNotifications")) {
      const errors = { ...phoneControl.errors }
      delete errors["phoneRequiredForNotifications"]
      phoneControl.setErrors(Object.keys(errors).length ? errors : null)
    }

    if (this.signupForm.invalid) {
      this.markFormGroupTouched()
      return
    }

    this.loading = true
    this.error = ""

    const userData = this.buildUserData()

    this.authService.register(userData).subscribe({
      next: (response) => {
        console.log("Register response:", response)
        this.loading = false

        if (response.status === "phone_verification_required") {
          this.phoneVerificationRequired = true
          this.pendingEmail = response.email ?? userData.email
          this.phoneOtpError = ""
          this.phoneOtpForm.reset()
          this.startResendCountdown()
          return
        }

        this.navigateToEmailOtp(userData.email)
      },
      error: (error) => {
        this.loading = false

        if (error.status === 409 && error.error?.message?.includes("phone")) {
          const duplicatePhoneMessage =
            "This phone number is already registered. Please use a different number."
          this.error = duplicatePhoneMessage
          this.snackbar.error(duplicatePhoneMessage, 5000)
          return
        }

        if (this.isExistingAccountError(error)) {
          this.error = "Email already exists. Please log in to continue."
          this.openExistingAccountDialog()
          return
        }

        let errorMessage = "Registration failed"
        if (error.error?.message) {
          errorMessage = error.error.message
        } else if (error.message) {
          errorMessage = error.message
        }

        this.error = errorMessage
        this.snackbar.error(errorMessage, 5000)
      },
    })
  }

  verifyPhone(): void {
    if (this.phoneOtpForm.invalid) {
      this.phoneOtpForm.get("otp")?.markAsTouched()
      return
    }

    this.phoneVerifyLoading = true
    this.phoneOtpError = ""

    const otp = this.phoneOtpForm.get("otp")?.value

    this.authService.verifyPhoneOtp(this.pendingEmail, otp).subscribe({
      next: () => {
        this.phoneVerifyLoading = false
        this.navigateToEmailOtp(this.pendingEmail)
      },
      error: () => {
        this.phoneVerifyLoading = false
        this.phoneOtpError = "Invalid or expired code. Try again."
        this.phoneOtpForm.get("otp")?.setValue("")
      },
    })
  }

  resendPhoneOtp(): void {
    if (!this.canResend) return

    this.resendLoading = true
    this.phoneOtpError = ""

    this.authService.resendPhoneOtp(this.pendingEmail).subscribe({
      next: () => {
        this.resendLoading = false
        this.snackbar.success("Code resent! Check your mobile.", 3000)
        this.startResendCountdown()
      },
      error: (error) => {
        this.resendLoading = false
        this.phoneOtpError = error.error?.message || "Failed to resend code. Please try again."
        this.snackbar.error(this.phoneOtpError, 5000)
      },
    })
  }

  onPhoneOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement
    const value = input.value.replace(/\D/g, "")
    if (value.length > 6) {
      input.value = value.slice(0, 6)
    }
    this.phoneOtpForm.get("otp")?.setValue(input.value)
  }

  private openExistingAccountDialog(): void {
    this.confirmDialog
      .open({
        title: "Account already exists",
        message: "Email already exists. Please log in to continue.",
        confirmLabel: "Login",
        cancelLabel: "Cancel",
        confirmIcon: "login",
      })
      .subscribe((goToLogin) => {
        if (goToLogin) {
          this.router.navigate(["/login"])
        }
      })
  }

  private isExistingAccountError(error: {
    status?: number
    error?: { message?: string; error?: string; code?: string }
    message?: string
  }): boolean {
    const raw = [
      error?.error?.message,
      error?.error?.error,
      error?.error?.code,
      error?.message,
    ]
      .filter((v): v is string => typeof v === "string" && v.length > 0)
      .join(" ")
      .toLowerCase()

    if (!raw) return false
    // Phone conflicts are handled separately and must not open the login dialog.
    if (raw.includes("phone")) return false

    return (
      raw.includes("already exists") ||
      raw.includes("already registered") ||
      raw.includes("usernameexistsexception") ||
      raw.includes("aliasexistsexception") ||
      raw.includes("user already") ||
      (raw.includes("email") && raw.includes("exist"))
    )
  }

  private startResendCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }

    this.countdown = 60
    this.canResend = false

    this.countdownTimer = setInterval(() => {
      this.countdown--
      if (this.countdown <= 0) {
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
        this.canResend = true
      }
    }, 1000)
  }

  private normalizePhone(phone: string): string {
    const cleaned = phone.replace(/[\s\-\(\)]/g, "")
    // Already has country code — use as-is
    if (cleaned.startsWith("+")) return cleaned
    // 10-digit number — add +91 (India testing; revert to +1 for US production)
    if (cleaned.length === 10) return "+1" + cleaned
    // Return as-is for anything else
    return cleaned
  }

  private buildUserData(): User {
    const rawPhone = this.signupForm.get("phonenumber")?.value
    const phoneNumber = rawPhone ? this.normalizePhone(rawPhone) : rawPhone

    return {
      accounttype: this.signupForm.get("accounttype")?.value,
      firstname: this.signupForm.get("firstname")?.value,
      lastname: this.signupForm.get("lastname")?.value,
      email: this.signupForm.get("email")?.value,
      password: this.signupForm.get("password")?.value,
      phonenumber: phoneNumber,
      textnotifications: this.signupForm.get("textnotifications")?.value || false,
      ...(this.signupForm.get("accounttype")?.value === "business" && {
        companyname: this.signupForm.get("companyname")?.value,
      }),
      ...this.buildPurposePayload(),
    }
  }

  private buildPurposePayload(): Pick<User, "signup_purpose_category_id" | "signup_purpose_other"> {
    if (this.signupForm.get("accounttype")?.value !== "individual") {
      return {}
    }
    const raw = this.signupForm.get("signup_purpose_category_id")?.value
    if (raw === SIGNUP_PURPOSE_OTHER) {
      return {
        signup_purpose_category_id: SIGNUP_PURPOSE_OTHER,
        signup_purpose_other: collapseRepeatedSpaces(
          this.signupForm.get("signup_purpose_other")?.value || "",
        ).slice(0, SIGNUP_PURPOSE_OTHER_MAX),
      }
    }
    const id = Number(raw)
    return {
      signup_purpose_category_id: Number.isFinite(id) && id > 0 ? id : null,
      signup_purpose_other: "",
    }
  }

  private navigateToEmailOtp(email: string): void {
    this.snackbar.success("Registration successful! Please check your email for verification code.", 5000)
    this.router.navigate(["/verify-otp"], {
      queryParams: {
        email,
        type: "signup",
      },
    })
  }

  private markFormGroupTouched(): void {
    Object.keys(this.signupForm.controls).forEach((key) => {
      this.signupForm.get(key)?.markAsTouched()
    })
  }

  get accountTypeControl() {
    return this.signupForm.get("accounttype")
  }

  get firstNameControl() {
    return this.signupForm.get("firstname")
  }

  get lastNameControl() {
    return this.signupForm.get("lastname")
  }

  get emailControl() {
    return this.signupForm.get("email")
  }

  get passwordControl() {
    return this.signupForm.get("password")
  }

  get confirmPasswordControl() {
    return this.signupForm.get("confirmPassword")
  }

  get phoneNumberControl() {
    return this.signupForm.get("phonenumber")
  }

  get companyNameControl() {
    return this.signupForm.get("companyname")
  }

  get signupPurposeControl() {
    return this.signupForm.get("signup_purpose_category_id")
  }

  get signupPurposeOtherControl() {
    return this.signupForm.get("signup_purpose_other")
  }

  get phoneOtpControl() {
    return this.phoneOtpForm.get("otp")
  }

  get maskedPhone(): string {
    const phone = this.signupForm.get("phonenumber")?.value
    if (!phone || typeof phone !== "string") return "***"
    const digits = phone.replace(/\D/g, "")
    if (digits.length < 3) return "***"
    return "***" + digits.slice(-3)
  }
}
